import { DynamicDrawUsage, InstancedMesh, Matrix4, Quaternion, Vector3 } from 'three';
import playthings from '../../data/playthings.generated.json';
import type { WorldFeature } from '../types';
import { SURFACE } from '../mesh/attributes';
import { createCitizenBatch } from './citizens/citizenBatch';
import { citizenGeometry } from './citizens/citizenShapes';
import { clipPoseAt, skinnedPoint, walkPose } from './citizens/citizenPose';
import { groundOf } from './crowd/walkNet';
import { alongLine, lineOf, type OnLine } from './crowd/walkLine';
import { WALKING } from './crowd/crowdSim';
import { createHeadings } from './heading';
import { planStreetPlay, type PlayKind } from './streetPlayPlan';
import { streetAssetGeometry, streetInstances } from './streetAsset';

const CLIPS: Record<PlayKind, string> = {
  hoop: 'RollHoop',
  top: 'PlayTop',
  stilts: 'WalkStilts',
  marbles: 'PlayMarbles',
  knuckles: 'PlayKnuckles',
};
const HOOP_RADIUS = 0.33;
const STILT_HEIGHT = 0.32;

/** Toys and their players share the actual rig pose, so hands and feet stay attached. */
export const streetPlayFeature: WorldFeature = {
  id: 'street-play',
  label: 'Children playing with street toys',
  weight: 0.3,
  build(ctx) {
    const asset = ctx.citizens,
      crowd = ctx.lifePlan?.crowd;
    if (!asset || !crowd) return { objects: [], animated: [], obstacles: [], counts: {} };
    const groups = planStreetPlay(ctx.layout, crowd, groundOf(ctx.layout, ctx.site, ctx.lots));
    if (!groups.length) return { objects: [], animated: [], obstacles: [], counts: {} };
    const figures = asset.figures.filter(
      (f) => f.trade === 'child_boy' || f.trade === 'child_girl'
    );
    if (!figures.length) throw new Error('street play: missing child figures');
    const actors = groups.flatMap((group, g) =>
      Array.from(
        { length: ['stilts', 'marbles', 'knuckles'].includes(group.kind) ? 2 : 1 },
        (_, member) => ({ g, member, size: 0.64 + ((g + member) % 3) * 0.025 })
      )
    );
    const batch = createCitizenBatch(
      ctx.materials.painted(),
      figures.map((f) => citizenGeometry(asset, f.lods[0]!)),
      actors.length,
      'street-playing-children'
    );
    ctx.materials.setCitizenPoses(asset.poses, asset.bones.length);
    const count = (kind: PlayKind) => groups.filter((g) => g.kind === kind).length;
    const capacities = {
      hoop: count('hoop'),
      top: count('top'),
      stick: count('hoop') + count('top'),
      cord: count('top'),
      stilt: count('stilts') * 4,
      step: count('stilts') * 4,
      marble: count('marbles') * 5,
      knuckle: count('knuckles') * 5,
    };
    const material = ctx.materials.painted({ instanced: true });
    const makeMesh = (name: keyof typeof playthings) => {
      const geometry = streetAssetGeometry(playthings[name], SURFACE.wood);
      const capacity = capacities[name];
      streetInstances(geometry, capacity, 2250);
      const mesh = new InstancedMesh(geometry, material, capacity);
      mesh.name = `play-${name}`;
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      return mesh;
    };
    const meshes = {
      hoop: makeMesh('hoop'),
      top: makeMesh('top'),
      stick: makeMesh('stick'),
      cord: makeMesh('cord'),
      stilt: makeMesh('stilt'),
      step: makeMesh('step'),
      marble: makeMesh('marble'),
      knuckle: makeMesh('knuckle'),
    };
    const lines = groups.map((g) =>
      lineOf(
        g.route.map(([x, y]) => [x, y, ctx.site.queries.groundHeightAt([x, y])]),
        WALKING.cornerM
      )
    );
    const headings = createHeadings(actors.length);
    const base = new Matrix4(),
      local = new Matrix4(),
      frame = new Matrix4();
    const scale = new Vector3(),
      hand = new Vector3(),
      foot = new Vector3(),
      end = new Vector3(),
      bottom = new Vector3(),
      top = new Vector3();
    const direction = new Vector3(),
      turn = new Quaternion(),
      xAxis = new Vector3(1, 0, 0);
    const at: OnLine = { x: 0, y: 0, ground: 0, heading: 0 };
    const palm = [0, 1.395 - Math.SQRT1_2 * 0.62, 0.165 + Math.SQRT1_2 * 0.62] as const;
    const point = (
      bone: string,
      place: readonly [number, number, number],
      row: number,
      out: Vector3
    ) => out.set(...skinnedPoint(asset, asset.bones.indexOf(bone), place, row)).applyMatrix4(base);
    const rod = (mesh: InstancedMesh, index: number, a: Vector3, b: Vector3) => {
      direction.subVectors(b, a);
      const length = direction.length();
      turn.setFromUnitVectors(xAxis, direction.multiplyScalar(1 / length));
      frame.compose(a, turn, scale.set(length, 1, 1));
      mesh.setMatrixAt(index, frame);
    };
    let day = true;
    const animated = {
      id: 'street-play-activities',
      setTimeOfDay(_minutes: number, activity: number) {
        day = activity > 0.05;
      },
      update(seconds: number) {
        headings.begin(seconds);
        batch.begin();
        for (const mesh of Object.values(meshes)) mesh.visible = day;
        const used = {
          hoop: 0,
          top: 0,
          stick: 0,
          cord: 0,
          stilt: 0,
          step: 0,
          marble: 0,
          knuckle: 0,
        };
        if (day)
          actors.forEach((actor, i) => {
            const group = groups[actor.g]!,
              line = lines[actor.g]!;
            const clip = asset.clips[CLIPS[group.kind]]!;
            const elapsed = seconds + actor.g * 1.7;
            const moving = group.kind === 'hoop' || group.kind === 'stilts';
            const distance =
              seconds * (group.kind === 'hoop' ? 1.65 : 0.52) +
              (actor.member * line.length) / 2 +
              actor.g;
            let x = group.center[0],
              y = group.center[1],
              heading = group.heading + actor.member * Math.PI;
            if (moving) {
              alongLine(
                line,
                (((distance % line.length) + line.length) % line.length) / line.length,
                at
              );
              x = at.x;
              y = at.y;
              heading = headings.at(i, at.heading);
            } else {
              const side = actor.member ? 0.75 : -0.75;
              x += Math.cos(group.heading) * side;
              y += Math.sin(group.heading) * side;
            }
            const ground = ctx.site.queries.groundHeightAt([x, y]);
            base
              .makeRotationY(heading)
              .scale(scale.setScalar(actor.size))
              .setPosition(x, ground + (group.kind === 'stilts' ? STILT_HEIGHT : 0), -y);
            const row = moving
              ? walkPose(clip, distance, actor.size)
              : clipPoseAt(clip, elapsed + actor.member * 1.5);
            batch.draw(i % figures.length, base, row);
            point('hand.R', palm, row, hand);
            if (group.kind === 'hoop') {
              const centre = end.set(1 / actor.size, 0, 0.12 / actor.size).applyMatrix4(base);
              centre.y = ctx.site.queries.groundHeightAt([centre.x, -centre.z]) + HOOP_RADIUS;
              local.makeRotationZ(-distance / HOOP_RADIUS);
              frame.makeRotationY(heading).multiply(local).setPosition(centre);
              meshes.hoop.setMatrixAt(used.hoop++, frame);
              end.x -= Math.cos(heading) * HOOP_RADIUS;
              end.z += Math.sin(heading) * HOOP_RADIUS;
              end.y += 0.1;
              rod(meshes.stick, used.stick++, hand, end);
            } else if (group.kind === 'stilts') {
              for (const side of [-1, 1]) {
                const bone = side > 0 ? 'R' : 'L';
                point(`foot.${bone}`, [0.105, 0.025, side * 0.09], row, foot);
                point(`hand.${bone}`, [palm[0], palm[1], side * palm[2]], row, hand);
                bottom
                  .copy(foot)
                  .add(
                    end.set(
                      Math.sin(heading) * side * 0.085,
                      -STILT_HEIGHT,
                      Math.cos(heading) * side * 0.085
                    )
                  );
                top.subVectors(hand, bottom).normalize().multiplyScalar(0.18).add(hand);
                rod(meshes.stilt, used.stilt++, bottom, top);
                frame.makeRotationY(heading).setPosition(foot.x, foot.y - 0.016, foot.z);
                meshes.step.setMatrixAt(used.step++, frame);
              }
            } else if (group.kind === 'top') {
              const centreGround = ctx.site.queries.groundHeightAt(group.center);
              frame
                .makeRotationY(elapsed * 24)
                .multiply(local.makeRotationZ(Math.sin(elapsed * 7) * 0.12))
                .setPosition(group.center[0], centreGround + 0.002, -group.center[1]);
              meshes.top.setMatrixAt(used.top++, frame);
              end.copy(hand).add(top.set(0.38, -0.04, 0));
              rod(meshes.stick, used.stick++, hand, end);
              bottom.set(
                group.center[0] + 0.25 * (1 + Math.sin(elapsed * Math.PI)),
                centreGround + 0.09,
                -group.center[1]
              );
              rod(meshes.cord, used.cord++, end, bottom);
            } else if (actor.member === 0) {
              const phase = (((elapsed % 3) + 3) % 3) / 3;
              for (let k = 0; k < 5; k++) {
                const angle = k * 2.399;
                let px = group.center[0] + Math.cos(angle) * 0.23;
                const py = group.center[1] + Math.sin(angle) * 0.23;
                let pz = -py;
                let height = ctx.site.queries.groundHeightAt([px, py]) + 0.022;
                if (group.kind === 'marbles' && k === 0)
                  px = group.center[0] + Math.sin(phase * Math.PI * 2) * 0.38;
                if (group.kind === 'knuckles' && k === 0) {
                  const flight = Math.max(0, Math.min(1, (phase - 0.12) / 0.55));
                  px = hand.x + (group.center[0] - hand.x) * flight;
                  pz = hand.z + (-py - hand.z) * flight;
                  height = hand.y + (height - hand.y) * flight + Math.sin(flight * Math.PI) * 0.45;
                }
                frame.makeRotationY(elapsed + k).setPosition(px, height, pz);
                const name = group.kind === 'marbles' ? 'marble' : 'knuckle';
                meshes[name].setMatrixAt(used[name]++, frame);
              }
            }
          });
        for (const mesh of Object.values(meshes)) mesh.instanceMatrix.needsUpdate = true;
        batch.end();
      },
    };
    animated.update(0);
    return {
      objects: [batch.mesh, ...Object.values(meshes)],
      animated: [animated],
      obstacles: [],
      counts: {
        playingChildren: actors.length,
        rollingHoops: count('hoop'),
        spinningTops: count('top'),
        stiltWalkers: count('stilts') * 2,
        marblePlayers: count('marbles') * 2,
        knucklebonePlayers: count('knuckles') * 2,
      },
    };
  },
};
